import { Injectable } from '@angular/core';
import { AuctionService, Offer, PurchaseActionService, BidHistory, MultiOfferHistoryRequest } from 'remarketing-angular-library';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, retry, switchMap } from 'rxjs/operators';
import { OfferHistory, PreviousOffers } from '../../models/offer-history.model';
import { flatten } from 'lodash';

interface OfferHistoryDto {
  offer: Offer;
  previousOffers: Offer[];
}

@Injectable({
  providedIn: 'root'
})
export class OffersService {
  constructor(
    private readonly auctionService: AuctionService,
    private readonly purchaseActionService: PurchaseActionService
  ) {}

  static mapOffersToOfferHistory(rootOffer: Offer, offerChain: Offer[]): OfferHistory {
    function compareOffers(o1: Offer, o2: Offer) {
      return new Date(o1.offerDate) < new Date(o2.offerDate) ? -1 : 1;
    }

    function reduceToPreviousOffers(carry: PreviousOffers, offer: Offer): PreviousOffers {
      const date = offer.offerDate.split('T')[0];
      if (!carry.offersByDate[date]) {
        carry.offersByDate = { ...carry.offersByDate, [date]: [] };
        carry.dates = [...carry.dates, date];
      }
      carry.offersByDate[date] = [...carry.offersByDate[date], offer];

      return carry;
    }

    const previousOffers = [...offerChain]
      .sort(compareOffers)
      .reduceRight(reduceToPreviousOffers, { dates: [], offersByDate: {} });

    return {
      offer: rootOffer,
      previousOffers
    };
  }

  static mapOffersToOfferHistoryIgnoreDate(rootOffer: Offer, offerChain: Offer[]): OfferHistory {
    // Order does not matter and the offers should not be separated by date.
    const date = 'date';
    return {
      offer: rootOffer,
      previousOffers: {
        dates: [date],
        offersByDate: {
          date: offerChain
        }
      } as PreviousOffers
    };
  }

  /**
   * Gets the auction item offers
   *
   * @param auctionItemUuid string
   */
  getAuctionItemOffers$(auctionItemUuid: string): Observable<Offer[]> {
    return this.auctionService.getAuctionItemOffers(auctionItemUuid).pipe(map(res => res.data));
  }

  /**
   * Get the audit logs for an offer
   *
   * @param offerUuid string
   */
  getOfferAuditLog$(offerUuid: string): Observable<Offer[]> {
    return this.auctionService.getOfferChain(offerUuid).pipe(
      catchError(() => of({ data: [] })),
      map(res => res.data)
    );
  }

  /**
   * Return the offer and its full history.
   *
   * @param auctionItemUuid string
   */
  getOfferHistory$(auctionItemUuid: string): Observable<OfferHistory[]> {
    return this.getAuctionItemOffers$(auctionItemUuid).pipe(
      // Flatten emitted observable
      mergeMap(offers => {

        if (offers.length === 0) {
          return of( undefined );
        }

        if( offers.length === 1 && !offers[0].offerUuid) {
          return of(
            offers.map(o => {
              return { offer: o, previousOffers: undefined };
            })
          );
        } else {
          // Create an array of Observables
          return forkJoin(offers.map(o => this.allNegotiationAuditLogs$(o.offerUuid, o.highestOfferAmount)));
        }
      }));
  }

  getMultipleOfferHistories$(offerHistoryRequest: MultiOfferHistoryRequest):  Observable<OfferHistory[]> {
    return this.auctionService.getAuctionItemsOfferHistory(offerHistoryRequest).pipe(map(res => {
      const offerHistories: OfferHistory[] = [];
      res.forEach((multiOfferHistory) => {
        const rootOffer = multiOfferHistory.offerHistory[0] || undefined;
        offerHistories.push(OffersService.mapOffersToOfferHistoryIgnoreDate(rootOffer, multiOfferHistory.offerHistory));
      });
      return offerHistories;
    }));
  }

  allNegotiationAuditLogs$(offerUuid: string, highestOfferAmount: number): Observable<OfferHistory> {
    // Get initial page
    // Merge all additional pages with initial page
    // Return mapped offer history
    // TODO: What should happen if we really can't retry any longer?
    return this.auctionService.getOfferChain(offerUuid, 50, 0).pipe(
      switchMap(res => {
        const { data, totalPages } = res;
        const start$ = of(data);
        const remainder$ = Array(totalPages - 1)
          .fill(0)
          .map((_, i) =>
            // Start with page # in order for retry to work
            of(i + 1).pipe(
              switchMap(page => this.auctionService.getOfferChain(offerUuid, 50, page)),
              retry(1),
              map(offers => offers.data)
            )
          );

        // Merge last emitted value of all observables and flatten
        return forkJoin(start$, ...remainder$).pipe(
          map(offerPages => {
            return flatten(offerPages);
          })
        );
      }),
      map((offers: Offer[]) => {
        const rootOffer = offers[0] || undefined;
        rootOffer.highestOfferAmount = highestOfferAmount;
        return OffersService.mapOffersToOfferHistory(rootOffer, offers);
      }),
      catchError(err => {
        return of({ offer: undefined, previousOffers: { dates: [], offersByDate: {} } });
      })
    );
  }
  /**
   * Get the bid hisotry for an bidItem
   *
   * @param auctionItemUuid string
   */
  getBidHistory(auctionItemUuid: string): Observable<BidHistory[]> {
    return this.auctionService.getBidHistory(auctionItemUuid).pipe(
      catchError(() => of({ data: [] })),
      map(res => res.data)
    );
  }
}
